<script setup lang="ts">
import getDepartmentAllParams from "@/types/responses/getDepartmentAllParams"
import ResizableList from '@/components/ResizableList.vue';
import { formatDate } from "@/services/functions"
import SelfAccount from "@/types/SelfAccount"
import DisplayFullnameFromUpdatedBy from '@/components/Common/DisplayFullnameFromUpdatedBy.vue';
import { computed } from "vue"
import i18n from "@/lang/i18n"

const props = defineProps<{
  departments: getDepartmentAllParams[]
  isLoading: boolean
  isSearchCompleted: boolean
  deleteAuthority: boolean
  selfAccount: SelfAccount
  isManagementOffice: boolean
  isDepartment: boolean
  openModal: (department: getDepartmentAllParams | null) => void
  deleteModal: (department: getDepartmentAllParams) => void
}>()

const HEADER = ([
  i18n.global.t('pages.departments.list.header.operationOffice'),
  i18n.global.t('pages.departments.list.header.firstLevel'),
  i18n.global.t('pages.departments.list.header.secondLevel'),
  i18n.global.t('pages.departments.list.header.updatedAt'),
  i18n.global.t('pages.departments.list.header.updatedBy'),
  i18n.global.t('pages.departments.list.header.form'),
  i18n.global.t('pages.departments.list.header.delete'),
])

const datetimeFormat = computed(() => (datetime: string) => {
  const d = new Date(datetime)
  return formatDate(d)
})

const headerClass = computed(() => (index: number) => {
  if (index == 5 || index == 6) return 'd-flex ml-4'
  return 'd-flex'
})

const isDelete = computed(() => (departmentId: number) => {
  if (!props.isDepartment) return true
  return departmentId != props.selfAccount.login_organization_id
})

const firstLevelDepartmentName = computed(() => (department: getDepartmentAllParams) => {
  if (!department.parent_department) return department.name
  return department.parent_department.name
})

const secondLevelDepartmentName = computed(() => (department: getDepartmentAllParams) => {
  if (!department.parent_department) return ''
  return department.name
})
</script>
<template>
  <div class="departments-list mb-2">
    <template v-if="props.isLoading">
      <div class="list-loading">
        <label>
          {{ i18n.global.t('pages.common.list.reading') }}
        </label>
      </div>
    </template>
    <template v-else-if="!props.isSearchCompleted && props.isManagementOffice">
      <div class="list-loading">
        <label>
          {{ i18n.global.t('pages.common.list.runFilter') }}
        </label>
      </div>
    </template>
    <ResizableList
      scrollAreaSelector=".list"
    >
      <div class="list list-scrollbar">
        <v-table>
          <thead>
            <tr>
              <th
                v-for="(item, index) in HEADER"
                :key="index"
                class="table-header column-space"
              >
                <div :class="headerClass(index)" >
                  {{ item }}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-if="departments?.length">
              <tr v-for="department in departments" :key="department.id">
                <td class="column-space">{{ department.operation_office.operation_office_name }}</td>
                <td class="column-space">{{ firstLevelDepartmentName(department) }}</td>
                <td class="column-space">{{ secondLevelDepartmentName(department) }}</td>
                <td class="column-space">{{ datetimeFormat(department.updated_at) }}</td>
                <td class="column-space">
                  <DisplayFullnameFromUpdatedBy
                    :updatedBy="department.updated_by"
                  />
                </td>
                <td class="column-space">
                  <v-btn
                    variant="text"
                    class="d-flex px-0"
                    @click="props.openModal(department)"
                  >
                    <span
                      class="material-symbols-outlined"
                    >
                      edit_note
                    </span>
                  </v-btn>
                </td>
                <td class="column-space">
                  <v-btn
                    v-if="props.deleteAuthority && isDelete(department.id)"
                    variant="text"
                    class="d-flex px-0"
                    @click="props.deleteModal(department)"
                  >
                    <span class="material-symbols-outlined delete-icon">
                      delete
                    </span>
                  </v-btn>
                </td>
              </tr>
            </template>
            <template v-else-if="!departments?.length && !props.isLoading && props.isSearchCompleted">
              <tr>
                <td colspan="9">
                  {{ i18n.global.t('pages.common.list.noData') }}
                </td>
              </tr>
            </template>
          </tbody>
        </v-table>
      </div>
    </ResizableList>
  </div>
</template>
<style>
.departments-list .list table {
  position: absolute;
}
.departments-list .list td{
  word-break: keep-all;
}
.departments-list .list th {
  min-width: 7em;
  position: -webkit-sticky;
  position: sticky;
  background-color: white;
  top: 0;
  z-index: 1;
}
</style>
<style scoped>
.departments-list {
  position: relative;
}
.departments-list .list{
  position: relative;
  min-width: 58vw;
  height: var(--resizable-height, var(--list-default-height));
  overflow: auto;
}
.departments-list table th {
  background-color: #f8f9fa;
  position: sticky;
  top: 0;
}
.departments-list .list .id-column{
  min-width: 2em;
}
.departments-list .list .updated-at-column{
  min-width: 10em;;
}
.checkbox-in-grid {
  padding: 1vm;
  height: inherit !important;
  display: inline-grid !important;
}
.list-loading {
  position: absolute;
  min-width: 58vw;
  height: 100%;
  overflow: auto;
  z-index: 1000;
  background-color: rgba(80,80,80,0.5);
  right: 0;
  left: 0;
}
.list-loading>label {
  font-weight: bold;
  color: white;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateY(-50%) translateX(-50%);
}
.delete-icon {
  color: red;
}
</style>
